import { supabase } from '@/integrations/supabase/client';
import { ServiceError } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';
import type { OrganizationICP, OrganizationICPInsert, OrganizationICPUpdate } from '@/types/database';

const serviceLogger = logger.child({ service: 'OrganizationICPService' });

export class OrganizationICPService {
  /**
   * Get organization's ICP
   */
  static async getICP(organizationId: string): Promise<OrganizationICP | null> {
    try {
      const { data, error } = await supabase
        .from('organization_icp')
        .select('*')
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      serviceLogger.error('Failed to fetch ICP', { error, organizationId });
      throw new ServiceError('Failed to fetch ICP', 'ICP_FETCH_ERROR', error);
    }
  }

  /**
   * Create organization ICP
   */
  static async createICP(data: OrganizationICPInsert): Promise<OrganizationICP> {
    try {
      const { data: icp, error } = await supabase
        .from('organization_icp')
        .insert(data)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!icp) throw new Error('No data returned from insert');

      serviceLogger.info('ICP created', { organizationId: data.organization_id });
      return icp;
    } catch (error) {
      serviceLogger.error('Failed to create ICP', { error, data });
      throw new ServiceError('Failed to create ICP', 'ICP_CREATE_ERROR', error);
    }
  }

  /**
   * Update organization ICP
   */
  static async updateICP(organizationId: string, updates: OrganizationICPUpdate): Promise<OrganizationICP> {
    try {
      const { data: icp, error } = await supabase
        .from('organization_icp')
        .update(updates)
        .eq('organization_id', organizationId)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!icp) throw new Error('No data returned from update');

      serviceLogger.info('ICP updated', { organizationId });
      return icp;
    } catch (error) {
      serviceLogger.error('Failed to update ICP', { error, organizationId });
      throw new ServiceError('Failed to update ICP', 'ICP_UPDATE_ERROR', error);
    }
  }

  /**
   * Upsert organization ICP (create or update)
   */
  static async upsertICP(data: OrganizationICPInsert): Promise<OrganizationICP> {
    try {
      const { data: icp, error } = await supabase
        .from('organization_icp')
        .upsert(data, { onConflict: 'organization_id' })
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!icp) throw new Error('No data returned from upsert');

      serviceLogger.info('ICP upserted', { organizationId: data.organization_id });
      return icp;
    } catch (error) {
      serviceLogger.error('Failed to upsert ICP', { error, data });
      throw new ServiceError('Failed to upsert ICP', 'ICP_UPSERT_ERROR', error);
    }
  }
}
